import { useState, useEffect, useCallback, useRef } from 'react'
import type { Editor } from '@tiptap/react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/utils/cn'
import { useTranslation } from '@/stores/i18nStore'
import { useAnalytics } from '@/hooks/useAnalytics'
import { useLookupWord, useSynonyms, useSpellingSuggestions } from '@/hooks/useDictionary'

interface ContextMenuPosition {
  x: number
  y: number
}

interface EditorContextMenuProps {
  editor: Editor
  containerRef: React.RefObject<HTMLDivElement>
}

export function EditorContextMenu({ editor, containerRef }: EditorContextMenuProps) {
  const { t } = useTranslation()
  const { trackSpellcheckFix, trackDictionaryLookup } = useAnalytics()
  const [isOpen, setIsOpen] = useState(false)
  const [position, setPosition] = useState<ContextMenuPosition>({ x: 0, y: 0 })
  const [selectedWord, setSelectedWord] = useState<string | null>(null)
  const [isMisspelled, setIsMisspelled] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // API hooks
  const lookupMutation = useLookupWord()
  const { data: synonymsData, isLoading: synonymsLoading } = useSynonyms(selectedWord)
  const { data: suggestionsData, isLoading: suggestionsLoading } = useSpellingSuggestions(
    isMisspelled ? selectedWord : null
  )

  // Handle right-click
  const handleContextMenu = useCallback((e: MouseEvent) => {
    const target = e.target as HTMLElement

    // Check if we're in the editor
    if (!containerRef.current?.contains(target)) {
      return
    }

    // Check if we clicked on a word
    const selection = window.getSelection()
    if (!selection) return

    // Get the word at click position
    let word = ''
    let isSpellingError = false

    // Check if clicked on a spellcheck error
    if (target.classList.contains('spellcheck-error')) {
      word = target.getAttribute('data-word') || target.textContent || ''
      isSpellingError = true
    } else {
      // Try to get word from selection or click position
      const range = document.caretRangeFromPoint(e.clientX, e.clientY)
      if (range) {
        // Expand selection to word
        const text = range.startContainer.textContent || ''
        const offset = range.startOffset

        // Find word boundaries (Amharic characters)
        const amharicRegex = /[\u1200-\u137F\u1380-\u139F\u2D80-\u2DDF]/
        let start = offset
        let end = offset

        // Find start of word
        while (start > 0 && amharicRegex.test(text[start - 1])) {
          start--
        }

        // Find end of word
        while (end < text.length && amharicRegex.test(text[end])) {
          end++
        }

        word = text.slice(start, end)
      }
    }

    if (word && word.length >= 2) {
      e.preventDefault()
      setSelectedWord(word)
      setIsMisspelled(isSpellingError)
      setPosition({ x: e.clientX, y: e.clientY })
      setIsOpen(true)
    }
  }, [containerRef])

  // Handle click outside to close
  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
      setIsOpen(false)
    }
  }, [])

  // Handle escape to close
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false)
    }
  }, [])

  useEffect(() => {
    document.addEventListener('contextmenu', handleContextMenu)
    document.addEventListener('click', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu)
      document.removeEventListener('click', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleContextMenu, handleClickOutside, handleKeyDown])

  // Replace word in editor
  const replaceWord = useCallback((replacement: string, isSpellFix: boolean = false) => {
    if (!selectedWord) return

    // Track if it's a spelling fix
    if (isSpellFix) {
      trackSpellcheckFix(selectedWord, replacement)
    }

    // Find and replace the word
    const { state } = editor
    const { doc } = state
    let replaced = false

    doc.descendants((node, pos) => {
      if (replaced) return false
      if (node.isText && node.text) {
        const index = node.text.indexOf(selectedWord)
        if (index !== -1) {
          const from = pos + index
          const to = from + selectedWord.length
          editor
            .chain()
            .focus()
            .deleteRange({ from, to })
            .insertContentAt(from, replacement)
            .run()
          replaced = true
          return false
        }
      }
    })

    setIsOpen(false)
  }, [editor, selectedWord, trackSpellcheckFix])

  // Look up word in dictionary
  const handleLookup = useCallback(async () => {
    if (!selectedWord) return
    const result = await lookupMutation.mutateAsync(selectedWord)
    trackDictionaryLookup(selectedWord, result?.found || false, result?.results?.length || 0)
    setIsOpen(false)
  }, [selectedWord, lookupMutation, trackDictionaryLookup])

  if (!isOpen || !selectedWord) return null

  return (
    <AnimatePresence>
      <motion.div
        ref={menuRef}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.1 }}
        className={cn(
          'fixed z-50 min-w-[200px] max-w-[300px]',
          'bg-white dark:bg-zinc-900',
          'border border-zinc-200 dark:border-zinc-700',
          'rounded-lg shadow-lg overflow-hidden'
        )}
        style={{
          left: position.x,
          top: position.y,
          maxHeight: '400px'
        }}
      >
        {/* Word header */}
        <div className="px-3 py-2 border-b border-zinc-100 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-800/50">
          <p className="text-sm font-medium text-zinc-900 dark:text-white font-ethiopic">
            "{selectedWord}"
          </p>
        </div>

        <div className="max-h-[300px] overflow-y-auto">
          {/* Spelling suggestions (if misspelled) */}
          {isMisspelled && (
            <div className="p-2 border-b border-zinc-100 dark:border-zinc-800">
              <p className="text-xs text-zinc-500 dark:text-zinc-400 px-2 mb-1">
                {t('spelling')}
              </p>
              {suggestionsLoading ? (
                <div className="px-2 py-1 text-sm text-zinc-400">Loading...</div>
              ) : suggestionsData?.suggestions && suggestionsData.suggestions.length > 0 ? (
                <div className="space-y-0.5">
                  {suggestionsData.suggestions.slice(0, 2).map((suggestion, idx) => (
                    <button
                      key={idx}
                      onClick={() => replaceWord(suggestion, true)}
                      className="w-full px-2 py-1.5 text-left text-sm rounded hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-700 dark:text-zinc-300 font-ethiopic"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="px-2 py-1 text-sm text-zinc-400">
                  No suggestions
                </div>
              )}
            </div>
          )}

          {/* Synonyms */}
          <div className="p-2 border-b border-zinc-100 dark:border-zinc-800">
            <p className="text-xs text-zinc-500 dark:text-zinc-400 px-2 mb-1">
              Synonyms
            </p>
            {synonymsLoading ? (
              <div className="px-2 py-1 text-sm text-zinc-400">Loading...</div>
            ) : synonymsData?.synonyms && synonymsData.synonyms.length > 0 ? (
              <div className="space-y-0.5">
                {synonymsData.synonyms.slice(0, 2).map((synonym, idx) => (
                  <button
                    key={idx}
                    onClick={() => replaceWord(synonym)}
                    className="w-full px-2 py-1.5 text-left text-sm rounded hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-700 dark:text-zinc-300 font-ethiopic"
                  >
                    {synonym}
                  </button>
                ))}
              </div>
            ) : (
              <div className="px-2 py-1 text-sm text-zinc-400">
                No synonyms found
              </div>
            )}
          </div>

          {/* Related words */}
          {synonymsData?.relatedWords && synonymsData.relatedWords.length > 0 && (
            <div className="p-2 border-b border-zinc-100 dark:border-zinc-800">
              <p className="text-xs text-zinc-500 dark:text-zinc-400 px-2 mb-1">
                Related
              </p>
              <div className="space-y-0.5">
                {synonymsData.relatedWords.slice(0, 2).map((word, idx) => (
                  <button
                    key={idx}
                    onClick={() => replaceWord(word)}
                    className="w-full px-2 py-1.5 text-left text-sm rounded hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-700 dark:text-zinc-300 font-ethiopic"
                  >
                    {word}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="p-2">
            <button
              onClick={handleLookup}
              disabled={lookupMutation.isPending}
              className="w-full px-2 py-1.5 text-left text-sm rounded hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-700 dark:text-zinc-300 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              Look up in dictionary
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  )
}
